import { AIR } from '@vm/shared';
import { family, M } from '../palette';
import { blocks, bricks, floorBoards, MapBuilder, patches, solid, speckle, stripes, type Brush, type DoorSpec, type Side } from './builder';
import { emptyMeta, type CoreRegion, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32; // ground surface (first air voxel)
/** West (team 0) Demolition core: a 1.6 m crystal inside the concrete vault behind the spawn yard. */
const CORE: CoreRegion = { team: 0, x0: 7, y0: G + 4, z0: 312, x1: 23, y1: G + 20, z1: 328 };

interface QuadStyle {
  wallA: Brush;
  wallB: Brush;
  wallC: Brush;
  roofA: Brush;
  roofC: Brush;
  trim: Brush;
  awning: number;
  car: number;
}

/** Mirrors a half-open x or z range about the map center. */
const mirrorRange = (a: number, b: number, m: boolean): [number, number] => (m ? [S - b, S - a] : [a, b]);
const mirrorSide = (side: Side, mx: boolean, mz: boolean): Side => {
  if (mx && side === 'e') return 'w';
  if (mx && side === 'w') return 'e';
  if (mz && side === 'n') return 's';
  if (mz && side === 's') return 'n';
  return side;
};

function quadrant(b: MapBuilder, mx: boolean, mz: boolean, st: QuadStyle): void {
  const X = (a: number, c: number): [number, number] => mirrorRange(a, c, mx);
  const Z = (a: number, c: number): [number, number] => mirrorRange(a, c, mz);
  const px = (x: number): number => (mx ? S - 1 - x : x);
  const pz = (z: number): number => (mz ? S - 1 - z : z);
  const door = (side: Side, at?: number): DoorSpec => ({ side: mirrorSide(side, mx, mz), at, w: 12, h: 22 });

  // House A: two floors, gable roof, door toward the side street.
  {
    const [x0, x1] = X(24, 124);
    const [z0, z1] = Z(24, 110);
    b.building({ x0, x1, z0, z1, y0: G - 1, floors: 2, wall: st.wallA, roof: st.roofA, trim: st.trim, doors: [door('s'), door('e')] });
  }
  // House B: three floors, flat roof with parapet.
  {
    const [x0, x1] = X(148, 282);
    const [z0, z1] = Z(24, 104);
    b.building({ x0, x1, z0, z1, y0: G - 1, floors: 3, wall: st.wallB, trim: st.trim, band: st.trim, roofStyle: 'flat', doors: [door('s', 20), door('s', 96)] });
  }
  // House C: two floors facing the plaza (kept clear of the spawn yards at z >= 25.6 m).
  {
    const [x0, x1] = X(24, 112);
    const [z0, z1] = Z(140, 244);
    b.building({ x0, x1, z0, z1, y0: G - 1, floors: 2, wall: st.wallC, roof: st.roofC, trim: st.trim, roofStyle: 'gableX', doors: [door('e', 30), door('n')] });
  }
  // Shop D: one floor with a big front window and an awning.
  {
    const [x0, x1] = X(140, 198);
    const [z0, z1] = Z(134, 198);
    b.building({
      x0, x1, z0, z1, y0: G - 1, floors: 1, wall: st.wallB, trim: st.trim, roofStyle: 'flat', parapet: 3,
      windows: { w: 16, h: 12, sill: 5, spacing: 22, glass: M('glass') },
      doors: [door('s'), door('e')],
    });
    const [ax0, ax1] = X(136, 202);
    const [az0, az1] = Z(198, 208);
    b.box(ax0, G + 24, az0, ax1, G + 25, az1, stripes(st.awning, M('cloth_white'), 4, 'x'));
  }
  // Garden, trees, crates and a parked car.
  b.tree(px(150), G, pz(236), 42, 15);
  b.tree(px(250), G, pz(150), 38, 13);
  b.crate(Math.min(px(214), px(223)), G, Math.min(pz(116), pz(125)));
  b.crate(Math.min(px(226), px(235)), G, Math.min(pz(116), pz(125)), 8);
  b.crate(Math.min(px(218), px(227)), G + 10, Math.min(pz(117), pz(126)), 8);
  b.barrel(px(130), G, pz(126));
  b.barrel(px(138), G, pz(122), M('paint_blue'));
  const [cx0] = X(150, 190);
  const [cz0] = Z(272, 290);
  b.car(cx0, G, cz0, st.car, true);
  b.fence(Math.min(px(24), px(112)), pz(124), Math.max(px(24), px(112)) + 1, pz(124), G);
  // Lamp posts along the street.
  for (const x of [60, 160, 260]) b.lamp(px(x), G, pz(292));
  b.bench(Math.min(px(70), px(83)), G, Math.min(pz(284), pz(287)));
}

function build(b: MapBuilder): MapMeta {
  const grass = patches(family('grass'), 0.05, 11);
  b.ground(G, grass, speckle(family('dirt')), speckle([M('rock_1'), M('rock_2')]));

  // Streets: E-W and N-S, with sidewalks.
  const asphalt = solid(family('asphalt'), 0);
  const sidewalk = blocks(family('concrete'), 5, 22);
  b.box(0, G - 1, 288, S, G, 352, sidewalk);
  b.box(288, G - 1, 0, 352, G, S, sidewalk);
  b.box(0, G - 1, 300, S, G, 340, asphalt);
  b.box(300, G - 1, 0, 340, G, S, asphalt);
  for (let x = 4; x < S; x += 24) {
    if (x > 200 && x < 440) continue;
    b.box(x, G - 1, 319, x + 12, G, 321, M('road_white'));
  }
  for (let z = 4; z < S; z += 24) {
    if (z > 200 && z < 440) continue;
    b.box(319, G - 1, z, 321, G, z + 12, M('road_white'));
  }

  // Central plaza.
  const cobble = blocks([M('cobble_1'), M('cobble_2'), M('stone_4')], 3, 23);
  b.box(206, G - 1, 206, 434, G, 434, cobble);
  b.box(206, G - 1, 206, 434, G, 208, M('stone_1'));
  b.box(206, G - 1, 432, 434, G, 434, M('stone_1'));
  b.box(206, G - 1, 206, 208, G, 434, M('stone_1'));
  b.box(432, G - 1, 206, 434, G, 434, M('stone_1'));

  const quadStyles: [boolean, boolean, QuadStyle][] = [
    [false, false, {
      wallA: bricks(family('brick_red'), 31), wallB: solid(family('plaster'), 32), wallC: bricks([M('brick_yellow'), M('sandstone_1')], 33),
      roofA: bricks(family('roof_red'), 34), roofC: bricks([M('roof_slate')], 35), trim: M('stone_2'), awning: M('cloth_red'), car: M('paint_red'),
    }],
    [false, true, {
      wallA: bricks([M('brick_orange'), M('brick_red_3')], 41), wallB: solid([M('plaster_2'), M('plaster_3')], 42), wallC: bricks([M('brick_gray'), M('stone_1')], 43),
      roofA: bricks([M('roof_slate')], 44), roofC: bricks(family('roof_red'), 45), trim: M('concrete_4'), awning: M('cloth_green'), car: M('paint_yellow'),
    }],
    [true, false, {
      wallA: bricks([M('brick_gray'), M('concrete_2')], 51), wallB: solid([M('plaster_1'), M('paint_white')], 52), wallC: bricks(family('brick_red'), 53),
      roofA: bricks([M('roof_blue')], 54), roofC: bricks([M('roof_slate')], 55), trim: M('stone_4'), awning: M('cloth_blue'), car: M('paint_blue'),
    }],
    [true, true, {
      wallA: bricks([M('brick_yellow'), M('brick_orange')], 61), wallB: solid([M('plaster_2'), M('plaster_1')], 62), wallC: bricks([M('brick_red_2'), M('brick_gray')], 63),
      roofA: bricks(family('roof_red'), 64), roofC: bricks([M('roof_blue')], 65), trim: M('stone_2'), awning: M('cloth_yellow'), car: M('paint_green'),
    }],
  ];
  for (const [mx, mz, st] of quadStyles) quadrant(b, mx, mz, st);

  // Clock tower in the plaza center: arches at ground level, stairs up to a rooftop nest.
  const towerWall = blocks([M('brick_gray'), M('stone_1'), M('stone_2')], 4, 71);
  const tower = b.building({
    x0: 290, x1: 350, z0: 290, z1: 350, y0: G - 1, floors: 4, floorH: 30, wallT: 3, wall: towerWall,
    trim: M('stone_4'), band: M('stone_4'), roofStyle: 'flat', parapet: 6,
    floor: floorBoards(family('planks_dark')),
    windows: { w: 8, h: 12, sill: 10, spacing: 20, glass: null },
    doors: [{ side: 'n', w: 20, h: 26 }, { side: 's', w: 20, h: 26 }, { side: 'e', w: 20, h: 26 }, { side: 'w', w: 20, h: 26 }],
  });
  // Clock faces on the top floor.
  const clockY = G + 102;
  for (const [x, z, alongX] of [[320, 289, true], [320, 350, true], [289, 320, false], [350, 320, false]] as const) {
    for (let dy = -7; dy <= 7; dy++)
      for (let da = -7; da <= 7; da++) {
        if (dy * dy + da * da > 49) continue;
        const v = dy * dy + da * da > 36 ? M('gold') : (da === 0 && dy >= 0 && dy < 6) || (dy === 0 && da >= 0 && da < 4) ? M('paint_black') : M('paint_white');
        if (alongX) b.set(x + da, clockY + dy, z, v);
        else b.set(x, clockY + dy, z + da, v);
      }
  }
  // Crenellations on the tower roof.
  for (let i = 0; i < 60; i += 6) {
    b.box(290 + i, tower.top + 6, 290, 293 + i, tower.top + 9, 293, M('stone_2'));
    b.box(290 + i, tower.top + 6, 347, 293 + i, tower.top + 9, 350, M('stone_2'));
    b.box(290, tower.top + 6, 290 + i, 293, tower.top + 9, 293 + i, M('stone_2'));
    b.box(347, tower.top + 6, 290 + i, 350, tower.top + 9, 293 + i, M('stone_2'));
  }

  // Powder kegs at two corners of the clock tower: one good hit starts the collapse.
  b.tntBarrel(284, G, 284);
  b.tntBarrel(356, G, 356);

  // Plaza furniture: planters with trees, market stalls, benches, lamps.
  for (const [x, z] of [[236, 236], [404, 236], [236, 404], [404, 404]]) {
    b.box(x - 8, G, z - 8, x + 8, G + 5, z + 8, M('stone_1'));
    b.box(x - 6, G + 4, z - 6, x + 6, G + 5, z + 6, M('dirt_1'));
    b.tree(x, G + 5, z, 40, 14);
  }
  b.stall(236, G, 262, M('cloth_red'));
  b.stall(384, G, 364, M('cloth_blue'));
  b.stall(262, G, 380, M('cloth_yellow'));
  b.stall(360, G, 244, M('cloth_green'));
  for (const [x, z] of [[216, 280], [216, 360], [424, 280], [424, 360]]) b.lamp(x, G, z, 34);
  b.bench(250, G, 214);
  b.bench(376, G, 422);
  b.bench(214, G, 300, false);
  b.bench(422, G, 326, false);

  // Spawn yards at the west and east ends, with low walls and crates for cover.
  for (const east of [false, true]) {
    const xa = east ? S - 70 : 10;
    const xb = east ? S - 10 : 70;
    b.box(xa, G - 1, 256, xb, G, 384, blocks(family('concrete'), 6, 81));
    const wx = east ? S - 72 : 70;
    b.box(wx, G, 256, wx + 2, G + 18, 296, bricks(family('brick_red'), 82));
    b.box(wx, G, 344, wx + 2, G + 18, 384, bricks(family('brick_red'), 83));
    b.box(wx - 1, G + 18, 256, wx + 3, G + 19, 296, M('stone_1'));
    b.box(wx - 1, G + 18, 344, wx + 3, G + 19, 384, M('stone_1'));
    b.crate(east ? S - 30 : 20, G, 258);
    b.crate(east ? S - 30 : 20, G, 372);
    // Team banners.
    b.box(east ? S - 6 : 4, G, 300, east ? S - 4 : 6, G + 40, 340, east ? M('team_blue') : M('team_red'));
    // Core vault against the perimeter wall: reinforced concrete around the team crystal, with a
    // barred slot facing the plaza (Demolition / Siege objective; decoration in other modes).
    const X = (a: number, c: number): [number, number] => (east ? [S - c, S - a] : [a, c]);
    const [vx0, vx1] = X(3, 27);
    b.box(vx0, G, 302, vx1, G + 24, 338, blocks([M('concrete_4')], 5, 84));
    b.box(vx0, G + 24, 300, vx1, G + 26, 340, M('plate'));
    const [cx0, cx1] = X(CORE.x0, CORE.x1);
    b.box(cx0, CORE.y0, CORE.z0, cx1, CORE.y1, CORE.z1, east ? M('core_blue') : M('core_red'));
    const [sx0, sx1] = X(23, 27);
    b.clear(sx0, G + 8, 316, sx1, G + 16, 324);
    for (let z = 316; z < 324; z += 3) b.box(east ? S - 26 : 25, G + 8, z, east ? S - 25 : 26, G + 16, z + 1, M('steel_dark_1'));
    const glow = east ? M('team_blue_glow') : M('team_red_glow');
    const [gx0, gx1] = X(26, 27);
    b.box(gx0, G + 20, 302, gx1, G + 21, 338, glow);
    b.box(gx0, G, 302, gx1, G + 20, 303, glow);
    b.box(gx0, G, 337, gx1, G + 20, 338, glow);
  }

  // Perimeter wall so the edge of the world reads as a place.
  const perim = bricks([M('brick_gray'), M('stone_2'), M('stone_4')], 91);
  b.walls(0, G, 0, S, G + 24, S, 3, perim);
  b.box(0, G + 24, 0, S, G + 26, 3, M('stone_1'));
  b.box(0, G + 24, S - 3, S, G + 26, S, M('stone_1'));
  b.box(0, G + 24, 0, 3, G + 26, S, M('stone_1'));
  b.box(S - 3, G + 24, 0, S, G + 26, S, M('stone_1'));
  // Street gates in the perimeter.
  b.clear(300, G, 0, 340, G + 24, 3);
  b.clear(300, G, S - 3, 340, G + 24, S);

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const west = [
    [3.2, 27], [3.2, 29.2], [3.2, 34.8], [3.2, 37], [5.5, 27.5], [5.5, 36.5], [5.4, 29.2], [6.4, 35],
  ];
  for (const [x, z] of west) {
    meta.spawns.push({ team: 0, x, y: gy, z, yaw: -Math.PI / 2 });
    meta.spawns.push({ team: 1, x: 64 - x, y: gy, z, yaw: Math.PI / 2 });
  }
  // FFA spawns scattered in streets and yards.
  const ffa = [
    [4, 30], [60, 34], [30, 4.5], [34, 59.5], [12, 12], [52, 12], [12, 52], [52, 52], [18, 31], [46, 33],
    [31, 18], [33, 46], [22.5, 22.5], [41.5, 41.5], [22.5, 41.5], [41.5, 22.5],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: gy, z, yaw: Math.atan2(x - 32, z - 32) });
  meta.points.push({ id: 'A', name: 'West Gate', x: 11, y: gy, z: 32, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Clock Tower', x: 32, y: gy + 0.1, z: 32, r: 2.6 });
  meta.points.push({ id: 'C', name: 'East Gate', x: 53, y: gy, z: 32, r: 3.5 });
  meta.flags.push({ team: 0, x: 4, y: gy, z: 32 });
  meta.flags.push({ team: 1, x: 60, y: gy, z: 32 });
  meta.cores.push({ ...CORE }, { team: 1, x0: S - CORE.x1, y0: CORE.y0, z0: CORE.z0, x1: S - CORE.x0, y1: CORE.y1, z1: CORE.z1 });
  for (const [x, z] of [[21, 21], [43, 21], [21, 43], [43, 43], [32, 24], [32, 40]]) meta.pickups.push({ kind: 'health', x, y: gy, z });
  for (const [x, z] of [[15, 32], [49, 32]]) meta.pickups.push({ kind: 'armor', x, y: gy, z });

  // Keep the voxel under every spawn clear of clutter.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    b.clear(x - 3, G, z - 3, x + 4, G + 18, z + 4);
    if (b.w.get(x, G - 1, z) === AIR) b.set(x, G - 1, z, M('stone_1'));
  }
  return meta;
}

export const brickPlaza: MapDef = {
  id: 'brick_plaza',
  name: 'Brick Plaza',
  description: 'A market town square around a clock tower. Bring it down.',
  size: [S, 192, S],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'gungame', 'demolition', 'siege', 'practice', 'sandbox'],
  players: [4, 12],
  seed: 1337,
  version: 4,
  theme: {
    skyTop: 0x4f8fe0,
    skyHorizon: 0xcfe6f7,
    fog: 0xc8def0,
    fogNear: 40,
    fogFar: 140,
    sun: [0.45, 0.8, 0.35],
    sunColor: 0xfff1d6,
    sunIntensity: 1.0,
    ambient: 0xb8d0ff,
    ambientIntensity: 0.55,
    outside: 0x5f9e3c,
    music: 'town',
  },
  build,
};
